import { Box, Button, Stack, SvgIcon, Tab, Tabs, useTheme, useMediaQuery } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from '../../assets';
import RecipesTab from './components/Recipe/RecipesTab';
import MadeInStoreTab from './components/MadeInStore/MadeInStoreTab';
import { RecipeDialogProvider, useRecipeDialog } from './context/RecipeDialogContext';
import useCurrencyCodeConfiguration from '../../hooks/configurations/useCurrencyCodeConfiguration';
import PageTitle from '../../components/shared/PageTitle';
import PageArea from '../../components/shared/PageArea';
import { UserContext } from '../../components/shared/useUser';

const enum TabEnum {
  Recipes = 1,
  MadeInStore = 2
}

const RecipeTabs = () => {
  const { t } = useTranslation('recipe');
  const theme = useTheme();

  const { hasPermissionTo } = useContext(UserContext);
  const [hasPermissionToReadMadeInStore, setHasPermissionToReadMadeInStore] = useState<boolean>(false);
  const [hasPermissionToCreateRecipe, setHasPermissionToCreateRecipe] = useState<boolean>(false);
  const [tabValue, setTabValue] = useState<number>(TabEnum.Recipes);
  const { openRecipeDialog } = useRecipeDialog();
  const { currencyCode, getCurrencyCodeConfiguration } = useCurrencyCodeConfiguration();

  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);

  useEffect(() => {
    getCurrencyCodeConfiguration();
    setHasPermissionToReadMadeInStore(hasPermissionTo(['MadeInStoreRead']));
    setHasPermissionToCreateRecipe(hasPermissionTo(['CreateItemsAndRecipes']));
  }, []);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const onCreateNew = () => {
    openRecipeDialog({ recipe: null, entityType: tabValue });
  };

  return (
    <PageArea>
      <Stack width='100%'
        spacing={2}
        direction={isMobile ? 'column' : 'row'}
        sx={{
          justifyContent: 'space-between',
        }}>
        <Box width={isMobile ? '100%' : 'auto'}>
          <PageTitle subTitle={t('subtitle')}>{t('recipes')}</PageTitle>
        </Box>

        {hasPermissionToCreateRecipe && (
          <Box width={isMobile ? '100%' : 'auto'}
            sx={{
              display: 'flex',
              justifyContent: { xs: 'flex-start', md: 'flex-end' },
            }}>
            <Button variant="primary"
              size="lg"
              startIcon={<SvgIcon> <Plus fill='white' /> </SvgIcon>}
              onClick={onCreateNew}
              sx={{
                mb: { xs: 4, md: 0 },
              }}
            >
              {t('createNew')}
            </Button>
          </Box>
        )}
      </Stack>
      <Box sx={{ pb: '12px' }}>
        <Tabs value={tabValue}
          onChange={handleChange}>
          <Tab label={t('recipes')}
            value={TabEnum.Recipes} />
          {hasPermissionToReadMadeInStore && <Tab label={t('madeInStore')}
            value={TabEnum.MadeInStore} />}
        </Tabs>
      </Box>
      {tabValue === TabEnum.Recipes && <RecipesTab currencyCode={currencyCode}
        onCreateNew={onCreateNew} />}
      {tabValue === TabEnum.MadeInStore && <MadeInStoreTab onCreateNew={onCreateNew} />}
    </PageArea>
  );
};

const RecipePage = () => {
  return (
    <RecipeDialogProvider>
      <RecipeTabs />
    </RecipeDialogProvider>
  );
};

export default RecipePage;